import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { usePostHog } from 'posthog-js/react';
import { IconWorld, IconCheck, IconSparkles } from '@tabler/icons-react';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import { startStravaConnect } from '../utils/stravaConnect';
import { validateEmailSyntax } from '../utils/emailValidation';
import './ActionsBar.css';

const MYTHIC_WORLD_VIDEO =
  'https://firebasestorage.googleapis.com/v0/b/mythic-maps.firebasestorage.app/o/assets%2Fitoshima-film-recut.mp4?alt=media';
const MYTHIC_WORLD_POSTER =
  'https://firebasestorage.googleapis.com/v0/b/mythic-maps.firebasestorage.app/o/assets%2Fitoshima-film-recut-poster.jpg?alt=media';

const EARLY_ACCESS_STORAGE_KEY = (uid) => `mythicmaps:mythicWorldEarlyAccess:${uid}`;
const EARLY_ACCESS_PENDING_KEY = 'mythicWorldEarlyAccessPending';

const ActionsBar = ({ user }) => {
  const { t } = useTranslation();
  const posthog = usePostHog();
  const videoContainerRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const [justRegistered, setJustRegistered] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [accountEmail, setAccountEmail] = useState('');
  const [hasAccountEmail, setHasAccountEmail] = useState(false);
  const [emailDismissed, setEmailDismissed] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [isSavingEmail, setIsSavingEmail] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [emailSaved, setEmailSaved] = useState(false);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );

    if (videoContainerRef.current) {
      observer.observe(videoContainerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const enrollUserInEarlyAccess = useCallback(async (uid, source, profileData = {}) => {
    const nowIso = new Date().toISOString();
    try {
      window.localStorage.setItem(EARLY_ACCESS_STORAGE_KEY(uid), '1');
      window.localStorage.removeItem(EARLY_ACCESS_PENDING_KEY);
    } catch (_) {
      /* ignore storage errors */
    }
    setIsRegistered(true);
    setJustRegistered(true);
    posthog?.capture('mythic_world_early_access_registered', { source });

    try {
      await updateDoc(doc(db, 'users', uid), {
        'settings.mythicWorldEarlyAccess': true,
        'settings.mythicWorldEarlyAccessAt': nowIso,
      });
    } catch (err) {
      console.error('Error saving Mythic World early access to user settings:', err);
    }

    try {
      if (typeof setDoc === 'function') {
        await setDoc(
          doc(db, 'mythicWorldWaitlist', uid),
          {
            uid,
            displayName: profileData.displayName || user?.displayName || null,
            email: profileData.email || user?.email || null,
            source,
            registeredAt: nowIso,
          },
          { merge: true }
        );
      }
    } catch (err) {
      console.warn('Could not write to mythicWorldWaitlist:', err);
    }
  }, [posthog, user?.displayName, user?.email]);

  useEffect(() => {
    if (!user?.uid) {
      setIsRegistered(false);
      setJustRegistered(false);
      setAccountEmail('');
      setHasAccountEmail(false);
      return;
    }

    let cancelled = false;
    try {
      if (window.localStorage.getItem(EARLY_ACCESS_STORAGE_KEY(user.uid)) === '1') {
        setIsRegistered(true);
      }
    } catch (_) {
      /* ignore */
    }

    (async () => {
      let pendingOAuth = false;
      try {
        pendingOAuth = window.localStorage.getItem(EARLY_ACCESS_PENDING_KEY) === '1';
      } catch (_) {
        /* ignore */
      }

      try {
        const snap = await getDoc(doc(db, 'users', user.uid));
        if (cancelled) return;
        const data = snap.exists() ? snap.data() : {};
        const alreadyInFirestore = Boolean(data?.settings?.mythicWorldEarlyAccess);
        const resolvedEmail = data?.email || user?.email || '';
        setAccountEmail(resolvedEmail);
        setHasAccountEmail(Boolean(resolvedEmail));

        if (alreadyInFirestore) {
          setIsRegistered(true);
          try {
            window.localStorage.setItem(EARLY_ACCESS_STORAGE_KEY(user.uid), '1');
            window.localStorage.removeItem(EARLY_ACCESS_PENDING_KEY);
          } catch (_) {
            /* ignore */
          }
        } else if (pendingOAuth) {
          await enrollUserInEarlyAccess(user.uid, 'home_landing_oauth', {
            displayName: data?.displayName || user?.displayName,
            email: resolvedEmail,
          });
        }
      } catch (err) {
        if (!cancelled && pendingOAuth) {
          await enrollUserInEarlyAccess(user.uid, 'home_landing_oauth');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.uid, user?.email, user?.displayName, enrollUserInEarlyAccess]);

  const handleEarlyAccessClick = async () => {
    if (isRegistered || isRegistering) return;

    if (!user?.uid) {
      try {
        window.localStorage.setItem(EARLY_ACCESS_PENDING_KEY, '1');
      } catch (_) {
        /* ignore */
      }
      posthog?.capture('mythic_world_early_access_clicked', { source: 'actions_bar_logged_out' });
      startStravaConnect('/art');
      return;
    }

    setIsRegistering(true);
    try {
      await enrollUserInEarlyAccess(user.uid, 'actions_bar', {
        email: accountEmail,
      });
    } finally {
      setIsRegistering(false);
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setEmailError('');
    const { valid, normalized } = validateEmailSyntax(emailInput);
    if (!valid) {
      setEmailError(t('emailRegistration.errorInvalidEmail', 'Please enter a valid email address'));
      return;
    }

    setIsSavingEmail(true);
    try {
      const changeAccountEmailFunc = httpsCallable(functions, 'changeAccountEmail');
      await changeAccountEmailFunc({ email: normalized });
      setAccountEmail(normalized);
      setEmailSaved(true);
      setHasAccountEmail(true);
      posthog?.capture('mythic_world_early_access_email_saved', { source: 'actions_bar' });
      if (user?.uid && typeof setDoc === 'function') {
        try {
          await setDoc(
            doc(db, 'mythicWorldWaitlist', user.uid),
            { email: normalized },
            { merge: true }
          );
        } catch (_) {
          /* ignore */
        }
      }
    } catch (err) {
      console.error('Error saving email for Mythic World early access:', err);
      setEmailError(t('settingsPage.emailChangeError', 'Failed to save email. Please try again.'));
    } finally {
      setIsSavingEmail(false);
    }
  };

  return (
    <div className={`challenge-banner mythic-world-banner ${isRegistered ? 'is-registered' : ''} ${justRegistered ? 'just-registered' : ''}`}>
      <div className="challenge-banner-video mythic-world-video" ref={videoContainerRef}>
        {isVisible ? (
          <video
            src={MYTHIC_WORLD_VIDEO}
            poster={MYTHIC_WORLD_POSTER}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            className="challenge-banner-video-el"
          />
        ) : (
          <img
            src={MYTHIC_WORLD_POSTER}
            alt=""
            className="challenge-banner-video-el"
          />
        )}
      </div>

      <div className="mythic-world-body">
        <div className="challenge-banner-text">
          <span className="mythic-world-eyebrow">
            <IconWorld size={13} />
            {t('actionsBar.worldEyebrow', 'Mythic World · Early Access')}
          </span>
          <span className="challenge-banner-title">
            {t('actionsBar.worldTitle', '3D video highlights of your activities, and a 3D world for every route.')}
          </span>
          <span className="challenge-banner-description">
            {t(
              'actionsBar.worldDescription',
              'Every route becomes a living 3D world of real terrain, streets, buildings, and trees. Get cinematic 3D highlight videos of your activities first, and train or relive any route in the world in an immersive 3D world.'
            )}
          </span>
        </div>

        <div className="mythic-world-footer">
          {!isRegistered ? (
            <div className="mythic-world-cta-row">
              <button
                type="button"
                className="challenge-banner-badge mythic-world-cta"
                onClick={handleEarlyAccessClick}
                disabled={isRegistering}
              >
                {isRegistering ? (
                  t('actionsBar.worldJoining', 'Joining...')
                ) : (
                  <>
                    <IconSparkles size={13} />
                    {t('actionsBar.worldCta', 'Get early access')}
                  </>
                )}
              </button>
              <span className="mythic-world-rollout-note">
                {t('actionsBar.worldRolloutNote', '3D highlight videos will roll out first.')}
              </span>
            </div>
          ) : (
            <div className="mythic-world-confirmation" role="status" aria-live="polite">
              <div className="mythic-world-confirmation-line">
                <IconCheck size={15} stroke={2.5} className="mythic-world-confirmation-check" />
                <span>
                  {hasAccountEmail && accountEmail
                    ? t(
                        'actionsBar.worldConfirmedShortEmail',
                        `You're on the early access list · we'll notify you at ${accountEmail}.`,
                        { email: accountEmail }
                      )
                    : t(
                        'actionsBar.worldConfirmedShort',
                        "You're on the early access list · 3D highlight videos will roll out first."
                      )}
                </span>
              </div>

              {user?.uid && !hasAccountEmail && !emailDismissed && !emailSaved && (
                <form className="mythic-world-email-prompt" onSubmit={handleEmailSubmit}>
                  <div className="mythic-world-email-row">
                    <input
                      type="email"
                      value={emailInput}
                      onChange={(e) => {
                        setEmailInput(e.target.value);
                        setEmailError('');
                      }}
                      placeholder={t('settingsPage.enterEmailPlaceholder', 'Add email for notification')}
                      className="mythic-world-email-input"
                      disabled={isSavingEmail}
                    />
                    <button
                      type="submit"
                      className="mythic-world-email-save"
                      disabled={isSavingEmail || !emailInput.trim()}
                    >
                      {isSavingEmail ? t('settingsPage.savingEmail', 'Saving...') : t('actionsBar.worldEmailSave', 'Notify me')}
                    </button>
                    <button
                      type="button"
                      className="mythic-world-email-skip"
                      onClick={() => setEmailDismissed(true)}
                      disabled={isSavingEmail}
                    >
                      {t('actionsBar.worldEmailLater', 'Later')}
                    </button>
                  </div>
                  {emailError && <span className="mythic-world-email-error">{emailError}</span>}
                </form>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ActionsBar;
